/**
 * @file    MainView.tsx
 * @scope   VIEW
 * @summary Main tab of the drawer: title, chart-type/trend selectors and every configuration
 *          section (questions/calculations, enterprise grid, query builder, color, text block,
 *          options and filters). Pure composition — owns no state.
 * @consumes DrawerContext (shared drawer values), the MainView section components
 * @produces JSX for the drawer's main tab
 * @sideEffects None of its own; every mutation is delegated through the props/handlers passed in.
 * @notes Shared values (customWidget, loading, currentAlias, isDiyWidgetEngine, currentOptionsAlias)
 *        are read from DrawerContext to avoid prop drilling; only hook results and local UI state
 *        that live in DiyDrawer are forwarded, grouped by the section they feed.
 */
import React from 'react';
import notShowHierarchyFilter from 'constants/notShowHierarchyFilter';
import { Skeleton, SkeletonVariants } from '@cx/ui';
import TextInput from 'core-ui/TextInput/TextInput';
import SelectChartInput from 'core-ui/SelectChartInput/SelectChartInput';
import SelectTrendInput, { TrendOptions } from 'core-ui/SelectTrendInput/SelectTrendInput';
import TextBlockControllers from 'components/TextBlockControllers/TextBlockControllers';
import QueryBuilder from 'components/QueryBuilder/QueryBuilder';
import { TextBlockInterface } from 'models/WidgetModel';
import { useDrawerContext } from '../../State/DrawerContext';
import {
  chartsWithTextControllers,
  chartsWithTrend,
  dataGridRulesWithTrend,
} from '../../common/constants/chartCapabilities';
import { renderEngineSection } from '../../DiyWidgetEngine/sections/engineSectionRegistry';
import QuestionsCalculationsSection, {
  QuestionsCalculationsSectionProps,
} from './sections/QuestionsCalculationsSection';
import ColorSection, { ColorSectionProps } from './sections/ColorSection';
import OptionsSection from './sections/OptionsSection';
import FiltersSection, { FiltersSectionProps } from './sections/FiltersSection';

export interface MainViewProps {
  /** Widget title input. */
  title: {
    required: boolean;
    onTitleChange: (title: string) => void;
    onRequiredChange: (required: boolean) => void;
  };
  /** Chart-type dropdown. */
  chartType: {
    options: any;
    onChange: (option: any) => void;
    hasEnterpriseV2Config: (config: any) => boolean;
  };
  /** Trend dropdown (only rendered when the chart supports trend). */
  trend: {
    options: any;
    onChange: (option: any) => void;
    selected?: TrendOptions;
    onCountChange: (count: any) => void;
    setSelected: (trend: TrendOptions) => void;
  };
  /** Question/calculation pickers (forwarded verbatim to the section). */
  questionsCalculations: QuestionsCalculationsSectionProps;
  /** Enterprise "build your data grid" accordion. */
  enterpriseGrid: {
    availableOptions: any;
    setSelectedPartition: (partition: any) => void;
    setCurrentTab: (tab: any) => void;
  };
  /** Medusa query builder. */
  query: {
    availableOptions: any;
    onChange: (querydata: any) => void;
  };
  /** Color / color-family picker (forwarded verbatim to the section). */
  color: ColorSectionProps;
  /** Text-block controllers (only for text widgets). */
  textBlock: {
    onDataChange: (data: any) => void;
    onValidChange: (valid: boolean) => void;
  };
  /** Options accordion. */
  options: {
    handlers: any;
  };
  /** Filters accordion (forwarded verbatim to the section). */
  filters: FiltersSectionProps;
}

/**
 * Renders the drawer's main tab. The parent decides whether this tab is active; when it is not,
 * it simply does not mount this component.
 */
const MainView: React.FC<MainViewProps> = ({
  title,
  chartType,
  trend,
  questionsCalculations,
  enterpriseGrid,
  query,
  color,
  textBlock,
  options,
  filters,
}) => {
  const {
    customWidget,
    loading,
    currentAlias,
    isDiyWidgetEngine,
    engineLayout,
    currentOptionsAlias,
  } = useDrawerContext();

  const isTextWidget = chartsWithTextControllers.includes(customWidget.alias);

  const showTrend = Boolean(
    trend.options &&
      trend.options.length &&
      (dataGridRulesWithTrend.includes(currentOptionsAlias(currentAlias?.alias) as string) ||
        chartsWithTrend.includes(currentAlias?.chartType as string) ||
        engineLayout?.showTrend)
  );

  // WHY `currentAlias` is required: until the chart type resolves we cannot know whether the
  // chart supports filters (Enterprise V2 and Medusa charts do not), so rendering them early
  // made the accordion flash and then disappear.
  const showFilters =
    Boolean(currentAlias) &&
    (engineLayout?.showLegacyFilters ?? true) &&
    !customWidget.hasMedusaQuery &&
    !notShowHierarchyFilter.includes(customWidget.alias) &&
    !currentAlias?.hasMedusaQuery;

  const showLegacyOptions = engineLayout?.showLegacyOptions ?? true;

  return (
    <div className="diy-drawer-contents">
      <div className="diy-drawer-section">
        {renderWidgetTitle(customWidget, title, isTextWidget, chartType, currentAlias)}
        {renderChartInput(chartType, currentAlias, loading, isDiyWidgetEngine, customWidget)}
        {renderTrendInput(trend, loading, showTrend)}
        <QuestionsCalculationsSection {...questionsCalculations} />
      </div>
      {renderEngineSection(engineLayout?.mainSection, enterpriseGrid)}
      {customWidget.hasMedusaQuery ? (
        <QueryBuilder
          customWidget={customWidget}
          availableOptions={query.availableOptions}
          onChange={(querydata) => query.onChange(querydata)}
        />
      ) : null}
      {customWidget.alias && <ColorSection {...color} />}
      {isTextWidget ? (
        <div className="diy-drawer-section">
          <TextBlockControllers
            textBlockData={{ ...(customWidget?.options?.textBlock as TextBlockInterface) }}
            setTextBlockData={textBlock.onDataChange}
            setTextContentIsValid={textBlock.onValidChange}
          />
        </div>
      ) : null}
      {showLegacyOptions ? <OptionsSection handlers={options.handlers} /> : null}
      {showFilters ? <FiltersSection {...filters} /> : null}
    </div>
  );
};

const renderWidgetTitle = (
  customWidget: any,
  title: any,
  isTextWidget: boolean,
  chartType: any,
  currentAlias: any
) => {
  if (chartType.options && currentAlias && !isTextWidget) {
    return (
      <TextInput
        customWidgetTitle={customWidget?.displayName}
        setWidgetTitle={title.onTitleChange}
        required={title.required}
        setRequired={title.onRequiredChange}
        maxLength={50}
      />
    );
  } else if (!isTextWidget) {
    return <Skeleton variant={SkeletonVariants.rectangular} className="mainview_Skeleton" />;
  }
  return null;
};

const renderChartInput = (
  chartType: any,
  currentAlias: any,
  loading: boolean,
  isDiyWidgetEngine: boolean,
  customWidget: any
) => {
  if (chartType.options && currentAlias) {
    return (
      <SelectChartInput
        loading={loading}
        options={chartType.options}
        selectChartType={chartType.onChange}
        defaultChartType={currentAlias}
        isDisabled={
          isDiyWidgetEngine && chartType.hasEnterpriseV2Config(customWidget.customWidgetConfig)
        }
      />
    );
  }
  return <Skeleton variant={SkeletonVariants.rectangular} className="mainview_Skeleton" />;
};

const renderTrendInput = (trend: any, loading: boolean, showTrend: boolean) => {
  if (loading || (showTrend && (!trend.options || !trend.options.length))) {
    return <Skeleton variant={SkeletonVariants.rectangular} className="mainview_Skeleton" />;
  } else if (showTrend) {
    return (
      <SelectTrendInput
        loading={loading}
        options={trend.options}
        selectTrend={trend.onChange}
        defaultTrend={trend.selected}
        selectCount={trend.onCountChange}
        selectedChart={trend.selected}
        setSelectedChartTrend={trend.setSelected}
      />
    );
  }
};

export default React.memo(MainView);
